import {
  ETIQUETA_DESTINO_VIAJE,
  ETIQUETA_DESTINO_VIAJE_EN,
  ETIQUETA_ESTADO_VIAJE,
  ETIQUETA_ESTADO_VIAJE_EN,
  type Viaje,
} from '@cnc/contracts';
import {et, tr, txt} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';
import {ayudaDeViaje} from './ayuda-inventario';
import {type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';

// Columnas de la tabla de Recolecciones (viajes de camion), con el mismo
// formato que la del Consolidado. `orden` es la columna por la que ordena el
// servidor. Conductor, cedula y placa solo existen para administradores.
const G = {
  principal: txt('Principal', 'Main'),
  fechas: txt('Fechas', 'Dates'),
  camion: txt('Camión', 'Truck'),
  revision: txt('Revisión', 'Review'),
};

export const COLUMNAS_PERSONALES_DE_VIAJE = ['conductor', 'cedula', 'placa'];

// Cada columna lleva su nota (i): que es y como se llena (Acciones no la necesita).
export const COLUMNAS_DE_VIAJES: ColumnaDeTabla<Viaje, string>[] = (
  [
    {
      id: 'recoleccion',
      titulo: txt('Recolección', 'Pickup'),
      grupo: G.principal,
      orden: 'recoleccion',
      porDefecto: true,
      ancho: 120,
      valor: v => textoOGuion(v.recoleccion),
    },
    {
      id: 'salida',
      titulo: txt('Salida de Quintana', 'Departure from Quintana'),
      grupo: G.fechas,
      orden: 'salida',
      porDefecto: true,
      ancho: 150,
      valor: v => fecha(v.salida) + (v.fechasOriginales ? ' *' : ''),
    },
    {
      id: 'descarga',
      titulo: txt('Ingreso a bodega Cartagena', 'Entry to Cartagena warehouse'),
      grupo: G.fechas,
      orden: 'descarga',
      porDefecto: true,
      ancho: 200,
      valor: v => fecha(v.descarga) + (v.fechasOriginales ? ' *' : ''),
    },
    {
      id: 'dias',
      titulo: txt('Días', 'Days'),
      grupo: G.fechas,
      orden: 'dias',
      porDefecto: true,
      numerica: true,
      ancho: 70,
      valor: v => (v.dias === null ? '—' : numero(v.dias)),
    },
    {
      id: 'destino',
      titulo: txt('Destino', 'Destination'),
      grupo: G.principal,
      orden: 'destino',
      porDefecto: true,
      ancho: 140,
      valor: v => (v.destino ? et(ETIQUETA_DESTINO_VIAJE, ETIQUETA_DESTINO_VIAJE_EN)[v.destino] : '—'),
    },
    {
      id: 'transportista',
      titulo: txt('Transportadora', 'Carrier'),
      grupo: G.camion,
      orden: 'transportista',
      porDefecto: true,
      ancho: 160,
      valor: v => textoOGuion(v.transportista),
    },
    {
      id: 'vehiculo',
      titulo: txt('Vehículo', 'Vehicle'),
      grupo: G.camion,
      orden: 'vehiculo',
      porDefecto: true,
      ancho: 100,
      valor: v => textoOGuion(v.tipoVehiculo),
    },
    {
      id: 'contenedor',
      titulo: txt('Contenedor terrestre', 'Inland container'),
      grupo: G.camion,
      orden: 'contenedor',
      porDefecto: true,
      ancho: 160,
      valor: v => textoOGuion(v.contenedorTerrestre),
    },
    {
      id: 'toneladas',
      titulo: txt('Toneladas', 'Tonnes'),
      grupo: G.principal,
      orden: 'toneladas',
      porDefecto: true,
      numerica: true,
      ancho: 100,
      valor: v => (v.toneladas === null ? '—' : numero(v.toneladas)),
    },
    {
      id: 'conductor',
      titulo: txt('Conductor', 'Driver'),
      grupo: G.camion,
      orden: 'conductor',
      porDefecto: true,
      ancho: 180,
      valor: v => textoOGuion(v.conductor),
    },
    {
      id: 'cedula',
      titulo: txt('Cédula', 'ID number'),
      grupo: G.camion,
      orden: 'cedula',
      porDefecto: true,
      ancho: 120,
      valor: v => textoOGuion(v.cedula),
    },
    {
      id: 'placa',
      titulo: txt('Placa', 'Plate'),
      grupo: G.camion,
      orden: 'placa',
      porDefecto: true,
      ancho: 100,
      valor: v => textoOGuion(v.placa),
    },
    {
      id: 'estado',
      titulo: txt('Estado', 'Status'),
      grupo: G.principal,
      orden: 'estado',
      porDefecto: true,
      ancho: 120,
      valor: v =>
        et(ETIQUETA_ESTADO_VIAJE, ETIQUETA_ESTADO_VIAJE_EN)[v.estado] +
        (v.motivoCancelacion ? ` · ${v.motivoCancelacion}` : ''),
    },
    {
      id: 'revision',
      titulo: txt('Para revisar', 'To review'),
      grupo: G.revision,
      porDefecto: true,
      ancho: 200,
      valor: v => (v.paraRevisar ? `⚠ ${v.notaRevision ?? tr('Revisar', 'Review')}` : '—'),
    },
    {
      id: 'notas',
      titulo: txt('Notas', 'Notes'),
      grupo: G.revision,
      porDefecto: true,
      ancho: 200,
      valor: v => textoOGuion(v.notas),
    },
    {
      id: 'editado',
      titulo: txt('Editado', 'Edited'),
      grupo: G.revision,
      porDefecto: true,
      ancho: 230,
      valor: v =>
        v.editadoEl
          ? tr(
              `Editado${v.editadoPor ? ` por ${v.editadoPor}` : ''} el ${fechaHora(v.editadoEl)}`,
              `Edited${v.editadoPor ? ` by ${v.editadoPor}` : ''} on ${fechaHora(v.editadoEl)}`,
            )
          : '—',
    },
    {
      id: 'acciones',
      titulo: txt('Acciones', 'Actions'),
      grupo: G.principal,
      porDefecto: true,
      ancho: 120,
      valor: () => '',
    },
  ] satisfies ColumnaDeTabla<Viaje, string>[]
).map(c => ({...c, ayuda: ayudaDeViaje(c.id)}));

export const DEFINICION_VIAJES: DefinicionDeColumnas<Viaje, string> = {
  clave: 'cnc.recolecciones.columnas',
  columnas: COLUMNAS_DE_VIAJES,
  grupos: [G.principal, G.fechas, G.camion, G.revision],
  fija: 'recoleccion',
};
